import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import RulesList from "./components/RulesList";
import RuleForm from "./components/RuleForm";
import RuleTestModal from "./components/RuleTestModal";
import { nextSortDirection } from "./utils/sortUtils";

import { fetchRfqNotificationRules, fetchRfqNotificationRuleDetails, clearCurrentRule, fetchRfqSources } from "actions/rfqNotifications";

import styles from "./ManageRules.module.scss";

const ManageRules = () => {
  const [showForm, setShowForm] = useState(false);
  const [selectedRule, setSelectedRule] = useState(null);
  const [showTestModal, setShowTestModal] = useState(false);
  const [testRuleId, setTestRuleId] = useState(null);

  // Server-side filter/sort state. Pagination (offset/limit/count) lives in the
  // redux store, set from each list response. All filters compose on the
  // server: name/ID, active-only, recipient-search, and sort.
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const [nameQuery, setNameQuery] = useState("");
  const [isActiveOnly, setIsActiveOnly] = useState(true);
  // "Show My Criterias" — off by default; restricts the list to the user's own rules.
  const [showMine, setShowMine] = useState(false);

  const location = useLocation();
  const dispatch = useDispatch();
  const permissions = useSelector((state) => state.permissions);
  const { rules, count, offset, limit, loading, error, currentRule, ruleDetailsLoading } =
    useSelector((state) => state.rfqNotifications);

  const canManageRules = permissions?.rfq_notification?.manage_rfq_notifications;
  const canViewAll = permissions?.rfq_notification?.view_all_rfq_notifications;
  const canManageAll = permissions?.rfq_notification?.manage_all_rfq_notifications;
  // "Show My Criterias" only matters when the user otherwise sees the whole company.
  const canFilterMine = Boolean(canViewAll || canManageAll);

  // Dispatches a list fetch using the current filter/sort state, allowing
  // per-call overrides (e.g. resetting offset to 0 on filter/sort change).
  const loadRules = (overrides = {}) => {
    dispatch(
      fetchRfqNotificationRules({
        sortField,
        sortDirection,
        name: nameQuery,
        isActive: isActiveOnly,
        // Only honor My-Criterias while the user can see others' rules; if the
        // permission goes away the hidden checkbox's stale state must not leak.
        mine: canFilterMine ? showMine : false,
        offset,
        limit,
        ...overrides,
      }),
    );
  };

  // Reset to list view when user navigates to this page (even from the same URL)
  useEffect(() => {
    setShowForm(false);
    setSelectedRule(null);
  }, [location.key]);

  // Initial load (first page, default order, active-only to match the default
  // checked toggle) once the user can manage rules.
  useEffect(() => {
    if (canManageRules) {
      dispatch(fetchRfqNotificationRules({ offset: 0, limit: 20, isActive: true }));
      dispatch(fetchRfqSources());
    }
  }, [dispatch, canManageRules]);

  const handleSort = (field) => {
    let nextField = field;
    let nextDirection;
    if (sortField === field) {
      nextDirection = nextSortDirection(sortDirection);
      if (!nextDirection) {
        // Cycled back past desc → return to the default server order.
        nextField = null;
      }
    } else {
      nextDirection = "asc";
    }
    setSortField(nextField);
    setSortDirection(nextDirection);
    loadRules({ sortField: nextField, sortDirection: nextDirection, offset: 0 });
  };

  const handleNameSearch = (value) => {
    setNameQuery(value);
    loadRules({ name: value, offset: 0 });
  };

  const handleActiveToggle = (checked) => {
    setIsActiveOnly(checked);
    loadRules({ isActive: checked, offset: 0 });
  };

  const handleMineToggle = (checked) => {
    setShowMine(checked);
    loadRules({ mine: checked, offset: 0 });
  };

  const handlePageChange = (newOffset) => {
    loadRules({ offset: newOffset });
  };

  const handleCreateRule = () => {
    dispatch(clearCurrentRule());
    setSelectedRule(null);
    setShowForm(true);
  };

  const handleEditRule = async (rule) => {
    dispatch(clearCurrentRule());
    setSelectedRule(rule);
    setShowForm(true);

    // Fetch full rule details for editing
    if (rule.id) {
      try {
        await dispatch(fetchRfqNotificationRuleDetails(rule.id));
      } catch (error) {
        console.error("Failed to fetch rule details:", error);
      }
    }
  };

  // Re-fetch after a delete instead of mutating local state. If the only rule
  // on a non-first page was just deleted, that page is now empty — step back to
  // the previous page so the user always lands on a valid, non-empty page
  // (offset/limit/rules come from the store and still reflect the pre-delete
  // page). The empty list correctly shows the empty state on page 1.
  const handleDeleteRule = () => {
    const stepBack = rules.length === 1 && offset > 0;
    const nextOffset = stepBack ? Math.max(0, offset - limit) : offset;
    loadRules({ offset: nextOffset });
  };

  const handleTestRule = (ruleId) => {
    setTestRuleId(ruleId);
    setShowTestModal(true);
  };

  const handleSaveRule = () => {
    // `selectedRule` is set only when editing an existing rule.
    const wasEditing = Boolean(selectedRule);
    setShowForm(false);
    setSelectedRule(null);
    if (wasEditing) {
      // The edited rule stays in place — keep the current page and sort.
      loadRules();
    } else {
      // A new rule lands at the top only under the default created-desc order,
      // so reset the sort to default AND jump to page 1 so it's visible even
      // when a non-default sort was active. (setState is async, so the null
      // sort is also passed explicitly to this fetch.)
      setSortField(null);
      setSortDirection(null);
      loadRules({ sortField: null, sortDirection: null, offset: 0 });
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setSelectedRule(null);
    // The form can mutate server state without going through Save (the
    // Deactivate/Activate toggle and schedule edits both call the API
    // directly), so re-fetch the current page on exit to keep the list's
    // Status column and active-only filter in sync.
    loadRules();
  };

  const handleCloseTestModal = () => {
    setShowTestModal(false);
    setTestRuleId(null);
  };

  if (showForm) {
    return (
      <div className={styles.wrapper}>
        <RuleForm
          rule={currentRule || selectedRule}
          loading={ruleDetailsLoading}
          onSave={handleSaveRule}
          onCancel={handleCancelForm}
          onTest={handleTestRule}
        />

        {showTestModal && (
          <RuleTestModal
            isOpen={showTestModal}
            ruleId={testRuleId}
            onClose={handleCloseTestModal}
          />
        )}
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.content}>
        <RulesList
          rules={rules}
          onEdit={handleEditRule}
          onDelete={handleDeleteRule}
          onTest={handleTestRule}
          loading={loading}
          error={error}
          onCreate={handleCreateRule}
          sortField={sortField}
          sortDirection={sortDirection}
          onSort={handleSort}
          count={count}
          offset={offset}
          limit={limit}
          onPageChange={handlePageChange}
          nameQuery={nameQuery}
          onNameSearch={handleNameSearch}
          isActiveOnly={isActiveOnly}
          onActiveToggle={handleActiveToggle}
          canFilterMine={canFilterMine}
          showMine={showMine}
          onMineToggle={handleMineToggle}
        />
      </div>

      {showTestModal && (
        <RuleTestModal
          isOpen={showTestModal}
          ruleId={testRuleId}
          onClose={handleCloseTestModal}
        />
      )}
    </div>
  );
};

export default ManageRules;
